<template>
  <section
    v-if="shouldShow"
    class="order-summary"
    :class="{
      'order-summary--multi': isCartMode,
      'order-summary--full-width-image': summaryImageFullWidth,
    }"
    aria-label="Resume de l'offre"
  >
    <!-- FORGE-412 — Header signature : eyebrow accent en haut de la carte
         pour identifier sans ambiguite "ce bloc resume ton choix actuel".
         En mode radio (1 offre max), label "Votre selection". En mode
         cart (multi-offres), label "Votre panier · N offre(s)" pour
         renforcer la metaphore panier et donner la volumetrie. -->
    <header v-if="headerLabel" class="order-summary__header">
      <span class="order-summary__header-icon" aria-hidden="true">
        <svg viewBox="0 0 512 512" aria-hidden="true" focusable="false">
          <path
            fill="currentColor"
            d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z"
          />
        </svg>
      </span>
      <span class="order-summary__header-label">{{ headerLabel }}</span>
    </header>
    <div v-if="pageName" class="order-summary__brand">{{ pageName }}</div>
    <!-- Bloc detaille PAR offre presente dans le cart : nom + prix +
         description detaillee (vanguardDescriptionDetail, ou fallback sur
         vanguardDescription si le detail n'a pas ete saisi cote
         Salesforce) avec son HTML d'origine (strong, bullets, br).
         Rendue via v-html apres simple decode URL + entites (cf.
         decodeHtml dans le script). Le contenu est trustable : il vient
         de Salesforce via productDetails.choices[].vanguardDescription*
         (champ saisi par des operateurs internes, pas par l'utilisateur
         final). Le champ `vanguardDescriptionDetail` etait
         historiquement affiche dans un tooltip Bootstrap sur la carte
         produit ; il a ete promu ici pour ameliorer la lisibilite des
         details d'offre (sans interaction necessaire). La description
         courte (`vanguardDescription`) reste sur la carte produit en
         texte plat. En mode radio (limitCartTo1Item=true), il y a au
         plus 1 bloc rendu — equivalent strict de l'ancien rendu
         mono-offre. En mode cart, il y en a autant que d'offres ajoutees
         au panier, chacune avec son propre nom + prix + bouton "Retirer"
         (si le cart le permet : cart.hideDelete=false). Le prix total
         agrege reste rendu plus bas dans la page par <carttotals />. -->
    <div
      v-for="item in summaryItems"
      :key="item.choice && item.choice.id"
      class="order-summary__item"
    >
      <div class="order-summary__item-body">
        <div
          v-if="item.imageSrc || item.showImagePlaceholder"
          class="order-summary__media"
          :class="{ 'order-summary__media--placeholder': !item.imageSrc }"
        >
          <img
            v-if="item.imageSrc"
            :src="item.imageSrc"
            :alt="item.lineName"
            class="order-summary__image"
          />
          <span
            v-else
            class="order-summary__image-placeholder"
            aria-hidden="true"
          >
            <svg viewBox="0 0 48 48" width="100%" height="100%">
              <rect
                x="6"
                y="10"
                width="36"
                height="30"
                rx="3"
                ry="3"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M6 32 L18 22 L26 28 L36 16 L42 22 L42 40 L6 40 Z"
                fill="currentColor"
                opacity="0.18"
              />
              <circle
                cx="16"
                cy="18"
                r="2.5"
                fill="currentColor"
                opacity="0.5"
              />
            </svg>
          </span>
        </div>
        <div class="order-summary__item-content">
          <!-- FORGE-412 — En mode cart, header item = nom a gauche +
           bouton retirer (chip icon-only) a droite, sur la meme ligne.
           Ca rattache visuellement le bouton a l'offre qu'il supprime,
           et libere la verticalite (vs. ancien bouton place sous la
           description avec un label texte). En mode radio, pas de
           bouton retirer (canRemove=false), donc le wrapper reste
           lineaire sans changement visuel. -->
          <div class="order-summary__item-header">
            <div v-if="item.lineName" class="order-summary__line-name">
              {{ item.lineName }}
            </div>
            <button
              v-if="item.canRemove"
              type="button"
              class="order-summary__remove"
              :title="$t('labels.deleteItem')"
              :aria-label="$t('labels.deleteItem')"
              @click="removeItem(item.cartItem)"
            >
              <svg
                class="order-summary__remove-icon"
                viewBox="0 0 448 512"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"
                />
              </svg>
            </button>
          </div>
          <div
            v-if="item.showPrice || item.showQuantity"
            class="order-summary__price-block"
          >
            <div
              v-if="item.showPrice"
              class="order-summary__price"
              :class="{
                'order-summary__price--starting': item.hasStartingPrice,
                'order-summary__price--simple': !item.hasStartingPrice,
              }"
            >
              <ChoicePrice :choice="item.choice" />
            </div>
            <div v-if="item.showQuantity" class="order-summary__qty-row">
              <div
                class="order-summary__qty"
                :data-quantity-select="item.choice.id"
              >
                <div class="order-summary__qty-control">
                  <select
                    :id="`order-summary-qty-${item.choice.id}`"
                    :value="item.cartItem.qty"
                    class="order-summary__qty-select"
                    :aria-label="$t('labels.qty')"
                    @input="onQtyChange(item.cartItem, $event)"
                  >
                    <option v-for="n in 15" :key="n" :value="n">
                      {{ n }}
                    </option>
                  </select>
                  <span class="order-summary__qty-label">{{
                    $t("labels.qty")
                  }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="item.subTextHtml"
            class="order-summary__description"
            v-html="item.subTextHtml"
          ></div>
          <div v-if="item.showQuickSell" class="order-summary__quick-sell">
            <CartTableUpsellList
              :cartItem="item.cartItem"
              :parent-choice-id="item.choice && item.choice.id"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useCartStore } from "@/store/cart";
import CartService from "@/services/cart";
import { stripDecodedHtml, decodeHtml } from "@/services/text";
import ChoicePrice from "@/components/choice/Price.vue";
import CartTableUpsellList from "@/components/cart/table/UpsellList.vue";
import PriceDisplay from "@/services/priceDisplay";
import ChoiceImage from "@/services/choiceImage";
import OrderPageOffers from "@/services/orderPageOffers";
import {
  choiceCatalogEntry,
  isCartItemQuantityEditable,
  validateCartItemQty,
} from "@/services/orderPageChoiceCatalog";

// Alias local : on garde le nom historique utilise dans les computed
// ci-dessous pour minimiser le diff.
const decodeAndStrip = stripDecodedHtml;

export default {
  name: "OrderSummary",
  components: {
    ChoicePrice,
    CartTableUpsellList,
  },
  data() {
    return {
      cart: null,
      // `windowReadyTick` est incremente au mounted (+ nextTick) pour forcer
      // les computed a relire `window.productDetails`. Necessaire en prod :
      // l'order-page de magellan-bundle injecte les vars globales via un
      // `<component :is="'script'">` qui ne s'execute qu'apres le premier
      // render. Sans dependance reactive, `v-if="shouldShow"` reste falsy.
      windowReadyTick: 0,
    };
  },
  computed: {
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    pageName() {
      void this.windowReadyTick;
      const pd = window.productDetails || {};
      const browserTitle = String(pd.browserTitle || "").trim();
      if (!browserTitle) return "";
      return decodeAndStrip(browserTitle) || browserTitle;
    },
    // En mode "panier" (limitCartTo1Item=false), l'utilisateur peut
    // ajouter plusieurs offres. On veut afficher dans le summary
    // l'integralite des offres ajoutees, pas seulement la 1ere. En
    // mode "radio" (limitCartTo1Item=true), il y a au plus 1 item dans
    // le cart, donc cette boucle rend exactement 1 bloc — strictement
    // equivalent au comportement historique.
    cartItems() {
      void this.windowReadyTick;
      const items = this.cart && this.cart.items;
      return Array.isArray(items) ? items : [];
    },
    // Mode "cart" (multi-offres) vs "radio" (1 offre max). On ne fait
    // pas confiance au seul nombre d'items au cart : un cart vide en
    // mode multi-offres doit deja afficher le label "Votre panier" pour
    // expliciter l'intention de la page.
    isCartMode() {
      void this.windowReadyTick;
      const pd = window.productDetails || {};
      return pd.limitCartTo1Item === false || pd.limitCartTo1Item === "false";
    },
    summaryImageFullWidth() {
      void this.windowReadyTick;
      return window.globalConfig?.summaryImageFullWidth === true;
    },
    // Label affiche dans le header signature. En mode radio, simple
    // "Votre selection". En mode cart, on suffixe avec le count
    // (singulier/pluriel) pour donner la volumetrie au lecteur.
    // Quand le cart est vide en mode cart (rare : aucun ajout fait
    // encore), on garde "Votre panier" sans count plutot que d'afficher
    // "0 offre" qui contredirait visuellement le contenu attendu.
    headerLabel() {
      void this.windowReadyTick;
      if (!this.isCartMode) {
        return this.$t("labels.summaryYourSelection");
      }
      const count = this.visibleCartItems.length;
      const base = this.$t("labels.summaryYourCart");
      if (count <= 0) return base;
      const noun =
        count > 1
          ? this.$t("labels.summaryOffers")
          : this.$t("labels.summaryOffer");
      return `${base} · ${count} ${noun}`;
    },
    // Determine si l'utilisateur peut retirer une offre du panier
    // depuis le summary. Repond a 3 contraintes cumulatives :
    //   1. La page autorise la suppression (cart.hideDelete=false, gere
    //      cote magellan/orderPage selon la conf brand).
    //   2. La page est en mode cart-mode (multi-offres). En mode radio,
    //      pas de bouton "Retirer" : l'utilisateur change d'offre en en
    //      selectionnant une autre, pas en supprimant l'actuelle.
    //   3. (implicite) un item est present — verifie au niveau de la
    //      boucle template.
    canRemoveItems() {
      if (this.cart && this.cart.hideDelete) return false;
      return this.isCartMode;
    },
    // Vrai quand aucun choix alternatif n'est possible sur la page (offre
    // unique configuree, ou choix force via hideListing). Dans ce cas la
    // liste d'offres est masquee : le quick-sell eventuel de l'offre
    // selectionnee est rendu ici, dans l'order-summary, comme source unique.
    noAlternativeChoice() {
      void this.windowReadyTick;
      const hideListing =
        new URLSearchParams(window.location.search).get("hideListing") ===
        "true";
      return OrderPageOffers.hasNoAlternativeChoice({
        productDetails: window.productDetails || {},
        hideListing,
      });
    },
    // La ligne compagnon d'une vente flash stack est facturee avec le
    // parent, mais elle n'est pas une offre du recap : le toggle sous
    // l'offre parente est sa representation.
    visibleCartItems() {
      return this.cartItems.filter(
        (item) => item && item.choice && !item.isQuickSellStack,
      );
    },
    // Items du cart pretraites pour le rendu summary : nom + prix +
    // description HTML extrait une fois par item, plutot que de le
    // refaire au milieu du template. Cela facilite aussi le test
    // (computed pur, sans dependance au DOM).
    summaryItems() {
      void this.windowReadyTick;
      const canRemove = this.canRemoveItems;
      const noAlternativeChoice = this.noAlternativeChoice;
      const productDetails = window.productDetails || {};
      const showImagePlaceholder = window.globalConfig?.previewMode === true;
      return this.visibleCartItems.map((cartItem) => {
        const choice =
          choiceCatalogEntry(cartItem, { orderPage: true }) || cartItem.choice;
        const rawName = choice.vanguardLabel || choice.name || "";
        const rawDescDetail =
          choice.vanguardDescriptionDetail || choice.vanguardDescription || "";
        const imageSrc = ChoiceImage.getChoiceImageSrc(
          cartItem,
          productDetails,
          { choice },
        );
        return {
          cartItem,
          choice,
          lineName: decodeAndStrip(rawName),
          imageSrc,
          showImagePlaceholder: showImagePlaceholder && !imageSrc,
          showQuantity: isCartItemQuantityEditable(cartItem, {
            orderPage: true,
          }),
          hasStartingPrice: this.hasStartingPrice(choice),
          showPrice: true,
          subTextHtml: decodeHtml(rawDescDetail),
          canRemove,
          showQuickSell:
            noAlternativeChoice &&
            OrderPageOffers.hasQuickSellForChoice(choice.id, productDetails),
        };
      });
    },
    shouldShow() {
      // En mode panier (multi-offres), on ne rend pas la carte tant
      // qu'aucune offre n'a ete ajoutee : sinon l'utilisateur voit un
      // encart "Votre panier" vide. En mode radio, un heading sans item
      // reste possible si un titre public est configure.
      if (this.isCartMode && this.summaryItems.length === 0) {
        return false;
      }
      return Boolean(this.pageName) || this.summaryItems.length > 0;
    },
  },
  mounted() {
    this.cart = useCartStore().cart;
    this.$nextTick(() => {
      this.windowReadyTick += 1;
    });
  },
  watch: {
    lastCartUpdate: {
      handler() {
        this.cart = { ...useCartStore().cart };
      },
    },
  },
  methods: {
    // Delegue a CartService pour beneficier de toute la logique cart
    // partagee (mise a jour du store Pinia, sync server-side, tracking,
    // upsells downgrade, etc.) — cf. CartTableChoiceList.deleteItemAction
    // qui utilise strictement le meme chemin.
    removeItem(cartItem) {
      if (!cartItem) return;
      CartService.deleteItem(cartItem);
    },
    onQtyChange(cartItem, event) {
      if (!cartItem) return;
      validateCartItemQty(cartItem, event);
      useCartStore().saveCart();
      CartService.updateCart();
    },
    hasStartingPrice(choice) {
      return PriceDisplay.shouldShowIntroOffer(choice);
    },
  },
};
</script>

<style scoped>
.order-summary__price-block {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 12px 0;
  width: 100%;
}

.order-summary__price {
  flex: 1 1 auto;
  min-width: 0;
}

.order-summary__qty-row {
  flex: 0 0 auto;
}

@media (max-width: 767.98px) {
  .order-summary__item-body {
    align-items: stretch;
  }

  .order-summary__item-content {
    width: 100%;
    align-self: stretch;
  }

  .order-summary__price-block {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .order-summary__qty-row {
    display: flex !important;
    flex: 0 0 auto;
    justify-content: flex-end !important;
    align-items: center;
    width: 100%;
    max-width: 100%;
    margin: 0 !important;
    padding: 0;
  }

  .order-summary__qty {
    flex: 0 0 auto;
    margin: 0 !important;
    width: auto !important;
    max-width: none;
  }
}
</style>
